import React, { useState, useMemo } from 'react';
import { message, Progress, Select, Button, Tag, Modal, Table, Popconfirm, Spin, Input } from 'antd';
import { searchDocuments } from '../../utils/services/templatesAPI';
import { deleteComplexDocument } from '../../utils/services/complexDocumentsAPI';

const { Option } = Select;
const { Search } = Input;

const CONCURRENCY_LIMIT = 1;

const runWithConcurrency = async (items, concurrency, processFn) => {
  let index = 0;
  const runNext = async () => {
    while (index < items.length) {
      const currentIndex = index++;
      await processFn(items[currentIndex], currentIndex);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, () => runNext()),
  );
};

const getTemplateId = (record) => record.documentId || record.id;
const getTemplateName = (record) => record.name || record.documentName || record.displayName || 'Unnamed';

const ManageTemplatesModal = ({ visible, onClose, categories }) => {
  const [categoryId, setCategoryId] = useState(null);
  const [fetchedTemplates, setFetchedTemplates] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [fetchingTemplates, setFetchingTemplates] = useState(false);
  const [deletingTemplates, setDeletingTemplates] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(null);
  const [searchText, setSearchText] = useState('');

  const filteredTemplates = useMemo(() => {
    if (!searchText.trim()) return fetchedTemplates;
    const query = searchText.trim().toLowerCase();
    return fetchedTemplates.filter((t) => {
      const name = getTemplateName(t).toLowerCase();
      const commId = (t.communicationId || '').toLowerCase();
      return name.includes(query) || commId.includes(query);
    });
  }, [fetchedTemplates, searchText]);

  const handleClose = () => {
    setCategoryId(null);
    setFetchedTemplates([]);
    setSelectedKeys([]);
    setDeleteProgress(null);
    setFetchingTemplates(false);
    setDeletingTemplates(false);
    setSearchText('');
    onClose();
  };

  const handleFetchTemplates = async () => {
    if (!categoryId) {
      message.error('Please select a category');
      return;
    }
    setFetchingTemplates(true);
    setFetchedTemplates([]);
    setSelectedKeys([]);
    setDeleteProgress(null);
    setSearchText('');
    try {
      const result = await searchDocuments({
        categoryId,
        limit: 2500,
        includeFlows: false,
        rgPermission: 'write',
      });
      const templates = Array.isArray(result?.data) ? result.data :
                        Array.isArray(result) ? result : [];
      setFetchedTemplates(templates);
      if (templates.length === 0) {
        message.info('No templates found for this category');
      } else {
        message.success(`Fetched ${templates.length} template(s)`);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Failed to fetch templates');
    } finally {
      setFetchingTemplates(false);
    }
  };

  const handleDeleteTemplate = async (documentId) => {
    try {
      setDeletingTemplates(true);
      await deleteComplexDocument(documentId);
      setFetchedTemplates((prev) => prev.filter((t) => getTemplateId(t) !== documentId));
      setSelectedKeys((prev) => prev.filter((k) => k !== documentId));
      message.success('Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error(`Failed to delete template: ${error.message}`);
    } finally {
      setDeletingTemplates(false);
    }
  };

  const performBulkDelete = async (ids) => {
    setDeletingTemplates(true);
    setDeleteProgress({ completed: 0, total: ids.length, failed: 0 });
    let completed = 0;
    let failed = 0;
    const failedIds = [];

    await runWithConcurrency(ids, CONCURRENCY_LIMIT, async (documentId) => {
      try {
        await deleteComplexDocument(documentId);
        completed++;
      } catch (error) {
        console.error(`Failed to delete template ${documentId}:`, error);
        failed++;
        failedIds.push(documentId);
      }
      setDeleteProgress({ completed: completed + failed, total: ids.length, failed });
    });

    setFetchedTemplates((prev) => prev.filter((t) => failedIds.includes(getTemplateId(t))));
    setSelectedKeys([]);
    setDeletingTemplates(false);

    if (failed === 0) {
      message.success(`Successfully deleted ${completed} template(s)`);
    } else {
      message.warning(`Deleted ${completed} template(s), ${failed} failed`);
    }
    setDeleteProgress(null);
  };

  const handleDeleteSelected = () => {
    if (selectedKeys.length === 0) {
      message.warning('No templates selected');
      return;
    }
    performBulkDelete(selectedKeys);
  };

  const handleDeleteAll = () => {
    if (fetchedTemplates.length === 0) {
      message.warning('No templates to delete');
      return;
    }
    performBulkDelete(fetchedTemplates.map((t) => getTemplateId(t)));
  };

  const handleSelectAll = () => {
    const allVisibleKeys = filteredTemplates.map((t) => getTemplateId(t));
    setSelectedKeys(allVisibleKeys);
  };

  const handleDeselectAll = () => {
    setSelectedKeys([]);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (_, record) => getTemplateName(record),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      width: 100,
      render: (text) => <Tag>{(text || '-').toUpperCase()}</Tag>,
    },
    {
      title: 'Communication ID',
      dataIndex: 'communicationId',
      key: 'communicationId',
      width: 160,
      ellipsis: true,
    },
    {
      title: 'Group ID',
      dataIndex: 'groupId',
      key: 'groupId',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      render: (_, record) => (
        <Popconfirm
          title="Delete this template?"
          onConfirm={() => handleDeleteTemplate(getTemplateId(record))}
          okText="Yes"
          cancelText="No"
          disabled={deletingTemplates}
        >
          <Button type="danger" size="small" disabled={deletingTemplates}>
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys) => setSelectedKeys(keys),
    getCheckboxProps: () => ({ disabled: deletingTemplates }),
  };

  const allVisibleSelected =
    filteredTemplates.length > 0 &&
    filteredTemplates.every((t) => selectedKeys.includes(getTemplateId(t)));

  return (
    <Modal
      title="Manage Templates - Bulk Delete"
      visible={visible}
      onCancel={handleClose}
      width={960}
      centered
      destroyOnClose
      footer={null}
      maskClosable={!deletingTemplates}
      closable={!deletingTemplates}
    >
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Select Category:
        </label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Select
            style={{ flex: 1 }}
            placeholder="Select a category to fetch templates"
            value={categoryId}
            onChange={(value) => {
              setCategoryId(value);
              setFetchedTemplates([]);
              setSelectedKeys([]);
              setDeleteProgress(null);
              setSearchText('');
            }}
            disabled={fetchingTemplates || deletingTemplates}
            showSearch
            optionFilterProp="children"
          >
            {categories.map((category) => (
              <Option key={category.id} value={category.id}>
                {category.name || `Category ${category.id}`}
              </Option>
            ))}
          </Select>
          <Button
            type="primary"
            onClick={handleFetchTemplates}
            loading={fetchingTemplates}
            disabled={!categoryId || deletingTemplates}
          >
            Fetch Templates
          </Button>
        </div>
      </div>

      {fetchedTemplates.length > 0 && (
        <>
          <div style={{ marginBottom: '12px' }}>
            <Search
              placeholder="Search by template name or communication ID"
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={deletingTemplates}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontWeight: 'bold' }}>
              {searchText.trim()
                ? `${filteredTemplates.length} of ${fetchedTemplates.length} template(s) shown`
                : `${fetchedTemplates.length} template(s) found`}
              {selectedKeys.length > 0 && (
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  {selectedKeys.length} selected
                </Tag>
              )}
            </span>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                size="small"
                onClick={allVisibleSelected ? handleDeselectAll : handleSelectAll}
                disabled={deletingTemplates || filteredTemplates.length === 0}
              >
                {allVisibleSelected ? 'Deselect All' : 'Select All'}
              </Button>
              <Popconfirm
                title={`Delete ${selectedKeys.length} selected template(s)?`}
                onConfirm={handleDeleteSelected}
                okText="Yes, Delete"
                cancelText="Cancel"
                disabled={selectedKeys.length === 0 || deletingTemplates}
              >
                <Button
                  type="primary"
                  size="small"
                  disabled={selectedKeys.length === 0 || deletingTemplates}
                >
                  Delete Selected ({selectedKeys.length})
                </Button>
              </Popconfirm>
              <Popconfirm
                title={`Delete ALL ${fetchedTemplates.length} template(s) in this category? This cannot be undone.`}
                onConfirm={handleDeleteAll}
                okText="Yes, Delete All"
                cancelText="Cancel"
                disabled={deletingTemplates}
                okType="danger"
              >
                <Button type="danger" size="small" disabled={deletingTemplates}>
                  Delete All ({fetchedTemplates.length})
                </Button>
              </Popconfirm>
            </div>
          </div>

          {deleteProgress && (
            <div style={{ marginBottom: '12px' }}>
              <Progress
                percent={Math.round((deleteProgress.completed / deleteProgress.total) * 100)}
                status={deleteProgress.failed > 0 ? 'exception' : 'active'}
                format={() => `${deleteProgress.completed}/${deleteProgress.total}`}
              />
              {deleteProgress.failed > 0 && (
                <span style={{ color: '#ff4d4f', fontSize: '12px' }}>
                  {deleteProgress.failed} failed
                </span>
              )}
            </div>
          )}

          <Table
            dataSource={filteredTemplates}
            columns={columns}
            rowKey={(record) => getTemplateId(record)}
            rowSelection={rowSelection}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
            scroll={{ y: 400 }}
            loading={deletingTemplates}
          />
        </>
      )}

      {fetchingTemplates && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" tip="Fetching templates..." />
        </div>
      )}

      {!fetchingTemplates && fetchedTemplates.length === 0 && categoryId && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          Click "Fetch Templates" to load templates for the selected category.
        </div>
      )}
    </Modal>
  );
};

export default ManageTemplatesModal;
